import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import moment from 'moment';
import { isMomentArray, isMomentPropType } from '../../lib/customPropTypes';
import { convertToMomentObject, isSameDay, allDaysDisabledBefore, allDaysDisabledAfter, getEffectiveMinDate, getEffectiveMaxDate, timezoneToOffset } from '../../lib/dateUtils';
import Month from './Month';
import Icon from '../Icon/Icon';
import Notice from '../Notice/Notice';

/**
 * `Calendar` allows you to select a date from a calendar. The component accepts and uses `Moment JS` date objects.
 *
 * This is the panel version of the `Calendar` component. {@link DateInput} and {@link DateTimeInput} uses this component in
 * their dropdowns.
 *
 * Any child components will be displayed at the bottom of the calendar, but only if neither of the todayButton or isClearable props are set to true.
 *
 * @property {string} className The class name of this component. Will be appended to "core-calendar". Add the class name
 * "light-header" to get a brighter header color that looks better on dark backgrounds.
 * @property {array} excluded List of dates (moment objects) that should be excluded (disabled).
 * @property {function} filtered Function that takes a moment as parameter and return true/false if the date should be
 * included. If false the date is disabled. Ex. isWeekday (date) { const day = date.day() return day !== 0 && day !== 6 }
 * @property {array} highlighted Function that takes a moment as parameter and return true/false if the date should be
 * highlighted.
 * @property {array} included List of dates (moment objects) that should be included. All other dates are disabled.
 * @property {boolean} isClearable Enables a clear button, that will set the date to NULL
 * @property {string} locale Locale string that decides the calendar language (name of months and days)
 * and start of week (start on sunday or monday).
 * @property {object|number|string} maxDate Max date allowed, all dates after max date are disabled
 * @property {object|number|string} minDate Min date allowed, all dates before min date are disabled
 * @property {function} onSelect On select day, retuns a moment object. NB! Must be implemented to the selected date in
 * update UI
 * @property {object|number|string} selected Selected date (moment/native Date object, date string or Unix timestamp in ms). If NULL or not passed the
 * calendar will open to today's date.
 * @property {string} todayButton If passed the calendar will enable a today button. Sets the date to today
 * @property {number} utcOffset Utc offset. If not passed the default is utc time 0.
 * @property {node} errorMessage An error message that will be shown below the input field. Defaults to no error message.
 * @property {node} successMessage A message that will be shown below the input field. Defaults to no message.
 * @property {node} infoMessage A message that will be shown below the input field. Defaults to no message.
 * @property {node} warningMessage A warning message that will be shown below the component. Defaults to no warning message.
 * @property {function} onClearNotice The callback function that will be called whenever the user has clicked to dismiss the optional Notice.
 * @see DateInput, DateTimeInput, TimeInput
 */
class Calendar extends Component {

  static displayName = 'Calendar';

  static propTypes = {
    className: PropTypes.string,
    excluded: PropTypes.arrayOf(isMomentArray),
    filtered: PropTypes.func,
    highlighted: PropTypes.func,
    included: PropTypes.arrayOf(isMomentArray),
    isClearable: PropTypes.bool,
    errorMessage: PropTypes.node,
    warningMessage: PropTypes.node,
    successMessage: PropTypes.node,
    infoMessage: PropTypes.node,
    onClearNotice: PropTypes.func,
    locale: PropTypes.string,
    maxDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.instanceOf(Date),
      isMomentPropType,
    ]),
    minDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.instanceOf(Date),
      isMomentPropType,
    ]),
    onBlur: PropTypes.func,
    onClose: PropTypes.func,
    onSelect: PropTypes.func.isRequired,
    selected: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.instanceOf(Date),
      isMomentPropType,
    ]),
    todayButton: PropTypes.bool,
    utcOffset: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number]
    ),
    children: PropTypes.node,
    onTodayButtonClick: PropTypes.func,
  };

  static defaultProps = {
    locale: moment.locale(),
    utcOffset: moment.utc().utcOffset()
  };

  static dateInView (props) {
    const { selected, openToDate, utcOffset } = props;
    const minDate = getEffectiveMinDate(props);
    const maxDate = getEffectiveMaxDate(props);
    const current = moment.utc().utcOffset(timezoneToOffset(utcOffset));
    if (selected) {
      return selected;
    } else if (minDate && maxDate && openToDate && openToDate.isBetween(minDate, maxDate)) {
      return openToDate;
    } else if (minDate && openToDate && openToDate.isAfter(minDate)) {
      return openToDate;
    } else if (minDate && minDate.isAfter(current)) {
      return minDate;
    } else if (maxDate && openToDate && openToDate.isBefore(maxDate)) {
      return openToDate;
    } else if (maxDate && maxDate.isBefore(current)) {
      return maxDate;
    } else if (openToDate) {
      return openToDate;
    }
    return current;
  }

  static localizeDate (date, props) {
    return moment(date).clone().locale(props.locale).utcOffset(timezoneToOffset(props.utcOffset));
  }

  constructor(props, context) {
    super(props, context);
    this.decreaseMonth = this.decreaseMonth.bind(this);
    this.decreaseYear = this.decreaseYear.bind(this);
    this.increaseMonth = this.increaseMonth.bind(this);
    this.increaseYear = this.increaseYear.bind(this);
    this.onClearClick = this.onClearClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onMonthKeyDown = this.onMonthKeyDown.bind(this);
    this.onYearKeyDown = this.onYearKeyDown.bind(this);
    this.onSelectDay = this.onSelectDay.bind(this);
    this.onTodayClick = this.onTodayClick.bind(this);
    this.renderCurrentMonth = this.renderCurrentMonth.bind(this);
    this.renderCurrentYear = this.renderCurrentYear.bind(this);
    this.renderWeekdaysHeader = this.renderWeekdaysHeader.bind(this);
    this.renderExtraButtons = this.renderExtraButtons.bind(this);

    const momentParsedProps = {
      ...props,
      minDate: convertToMomentObject(props.minDate),
      maxDate: convertToMomentObject(props.maxDate),
    }

    this.state = {
      date: Calendar.localizeDate(Calendar.dateInView(momentParsedProps), momentParsedProps),
    };
  }

  componentDidUpdate (prevProps) {
    const selected = convertToMomentObject(prevProps?.selected);
    const nextSelected = convertToMomentObject(this.props?.selected);

    const momentParsedProps = {
      ...this.props,
      minDate: convertToMomentObject(this.props.minDate),
      maxDate: convertToMomentObject(this.props.maxDate),
    }

    if ((nextSelected && !isSameDay(nextSelected, selected))
      || momentParsedProps.locale !== prevProps.locale
      || momentParsedProps.utcOffset !== prevProps.utcOffset)
    {
      this.setState({
        date: Calendar.localizeDate(Calendar.dateInView(momentParsedProps), momentParsedProps),
      });
    }
  }

  onClearClick() {
    this.props.onSelect(null);
  }

  onKeyDown(e) {
    if (e.keyCode === 9 || e.keyCode === 13 || e.keyCode === 27) {
      if (this.props.onClose) {
        this.props.onClose();
      }
      if (this.props.onBlur) {
        this.props.onBlur();
      }
    }
    return true;
  }

  onMonthKeyDown(e) {
    if (e.keyCode === 39) {
      e.stopPropagation();
      this.increaseMonth(e);
    } else if (e.keyCode === 37) {
      e.stopPropagation();
      this.decreaseMonth(e);
    } else if (e.keyCode === 40 || e.keyCode === 13 || e.keyCode === 9) {
      if (!e.shiftKey) {
        e.stopPropagation();
        e.preventDefault();
        this.refs.currentYear.focus();
      }
    } else if (e.keyCode === 27) {
      this.refs.mycalendar.focus();
    }
    return true;
  }

  onYearKeyDown(e) {
    if (e.keyCode === 39) {
      e.stopPropagation();
      this.increaseYear(e);
    } else if (e.keyCode === 37) {
      e.stopPropagation();
      this.decreaseYear(e);
    } else if (e.keyCode === 38) {
      e.stopPropagation();
      this.refs.currentMonth.focus();
    } else if (e.keyCode === 9 && e.shiftKey) {
      e.stopPropagation();
      e.preventDefault();
      this.refs.currentMonth.focus();
    } else if (e.keyCode === 9 || e.keyCode === 13 || e.keyCode === 27) {
      this.refs.mycalendar.focus();
    }
    return true;
  }

  onSelectDay (day) {
    this.props.onSelect(day.startOf('date'));
  }

  onTodayClick() {
    this.props.onSelect(moment.utc().utcOffset(timezoneToOffset(this.props.utcOffset)).startOf('date'));
  }

  increaseMonth (e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ date: this.state.date.clone().add(1, 'month') });
  }

  decreaseMonth (e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ date: this.state.date.clone().subtract(1, 'month') });
  }

  increaseYear (e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ date: this.state.date.clone().add(1, 'year') });
  }

  decreaseYear (e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ date: this.state.date.clone().subtract(1, 'year') });
  }

  renderCurrentMonth() {
    return (
      <div ref="currentMonth" className="calendar_current-month" onKeyDown={this.onMonthKeyDown} data-testid="current-month">
        <div className="previous-month">
          {!allDaysDisabledBefore(this.state.date, 'month', this.props) ?
            <Icon name="arrow-left" onClick={this.decreaseMonth} /> : null
          }
        </div>
        <div className="month-name">
          <span className="current-label">{this.state.date.format('MMMM')}</span>
        </div>
        <div className="next-month">
          {!allDaysDisabledAfter(this.state.date, 'month', this.props) ?
            <Icon name="arrow-right" onClick={this.increaseMonth}/> : null
          }
        </div>
      </div>
    );
  }

  renderCurrentYear() {
    return (
      <div ref="currentYear" className="calendar_current-year" onKeyDown={this.onYearKeyDown} data-testid="current-year">
        <div className="previous-year">
          {!allDaysDisabledBefore(this.state.date, 'year', this.props) ?
            <Icon name="arrow-left" onClick={this.decreaseYear} /> : null
          }
        </div>
        <div className="year-name">
          <span className="current-label">{this.state.date.format('YYYY')}</span>
        </div>
        <div className="next-year">
          {!allDaysDisabledAfter(this.state.date, 'year', this.props) ?
            <Icon name="arrow-right" onClick={this.increaseYear}/> : null
          }
        </div>
      </div>
    );
  }

  renderWeekdaysHeader () {
    const startOfWeek = this.state.date.clone().startOf('week');
    const weekdays = [];
    for (let i = 0; i < 7; i++) {
      const day = startOfWeek.clone().add(i, 'days');
      weekdays.push(
        <th key={i} className="weekdays-name">
          {day.localeData().weekdaysMin(day)}
        </th>
      );
    }
    return (<thead><tr>{weekdays}</tr></thead>);
  }

  renderExtraButtons() {
    const { minDate, maxDate, onTodayButtonClick } = this.props;
    const today = moment();
    const todayOutOfRange = (minDate && today.isBefore(minDate, 'day')) || (maxDate && today.isAfter(maxDate, 'day'));
    return (
      <div className="calendar_buttons">
        {this.props.isClearable ? <button onClick={this.onClearClick}>Clear</button> : null}
        {this.props.todayButton ? <button onClick={onTodayButtonClick || this.onTodayClick} disabled={todayOutOfRange}>Today</button> : null}
      </div>
    );
  }

  render () {
    const {
      excluded,filtered,highlighted,included, minDate, maxDate, selected, utcOffset,todayButton, isClearable,
      errorMessage, infoMessage, onClearNotice, successMessage, warningMessage, children
    } = this.props;
    return (
      <div ref="mycalendar" className={cx("core-calendar", this.props.className)} tabIndex="-1" onKeyDown={this.onKeyDown}>
        <div className="calendar_header">
          {this.renderCurrentMonth()}
          {this.renderCurrentYear()}
        </div>
        <div className="calendar_table">
          <table>
            {this.renderWeekdaysHeader()}
            <Month
              day={this.state.date}
              excluded={excluded}
              filtered={filtered}
              highlighted={highlighted}
              included={included}
              minDate={convertToMomentObject(minDate)}
              maxDate={convertToMomentObject(maxDate)}
              onDayClick={this.onSelectDay}
              selected={selected}
              utcOffset={timezoneToOffset(utcOffset)}/>
          </table>
        </div>
        {(todayButton || isClearable) ? this.renderExtraButtons() : children}
        <Notice
          errorMessage={errorMessage}
          warningMessage={warningMessage}
          successMessage={successMessage}
          infoMessage={infoMessage}
          onClear={onClearNotice}/>
      </div>
    );
  }

}

export default Calendar;
